import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { BgMasterListItem } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { WorkflowGenerationTargetCandidate } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'

import {
  type CivilDate,
  dateToCivilDate,
  protoDateToCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  WORKFLOW_TARGET_CODE_KIND_RANK,
  type WorkflowTargetCodeKind,
  workflowTargetCodeKindOfBgLicense,
} from '~/pages/WorkflowCases/models/workflowTargetCodeKind'

/**
 * 作成ダイアログの BG コード欄の候補 1 行。BG マスタの行と発電の計画提出者コードの
 * 候補 (`ListWorkflowGenerationTargetCandidates`) を同じ形に揃え、エリア・対象日の
 * 絞り込みと失効警告を共通にする。
 */
export type WorkflowTargetCandidate = {
  code: string
  name: string
  areaCode: AreaCode
  /** 欠落は暦日不正として、どの日にも有効でない扱いにする (fail-closed)。 */
  validFrom: CivilDate | undefined
  validTo: CivilDate | undefined
  kind: WorkflowTargetCodeKind
}

/**
 * BG コード欄の候補の母集合を作る (spec AC-WFW-003j)。発電の候補を含めるかは
 * 雛形の `allowsGenerationTargetCode` (backend の導出値) で caller が決め、
 * 含めない時は空配列を渡す。並びは 種別 (需要BG → 自己託送BG → 発電) → コード
 * (design `demand/cases.html` `_bgsForArea`)。
 */
export function buildWorkflowTargetCandidates(
  bgMasterItems: ReadonlyArray<
    Pick<
      BgMasterListItem,
      'bgCode' | 'bgName' | 'areaCode' | 'licenseType' | 'validFrom' | 'validTo'
    >
  >,
  generationCandidates: ReadonlyArray<
    Pick<
      WorkflowGenerationTargetCandidate,
      'submitterCode' | 'submitterName' | 'areaCode' | 'validFrom' | 'validTo'
    >
  >,
): WorkflowTargetCandidate[] {
  const bgs = bgMasterItems.flatMap((bg): WorkflowTargetCandidate[] => {
    const kind = workflowTargetCodeKindOfBgLicense(bg.licenseType)
    if (kind === undefined) return []
    return [
      {
        code: bg.bgCode,
        name: bg.bgName,
        areaCode: bg.areaCode,
        validFrom: protoDateToCivilDate(bg.validFrom),
        validTo: protoDateToCivilDate(bg.validTo),
        kind,
      },
    ]
  })
  const generations = generationCandidates.map(
    (g): WorkflowTargetCandidate => ({
      code: g.submitterCode,
      name: g.submitterName,
      areaCode: g.areaCode,
      validFrom: protoDateToCivilDate(g.validFrom),
      validTo: protoDateToCivilDate(g.validTo),
      kind: 'generation',
    }),
  )
  return [...bgs, ...generations].sort(
    (a, b) =>
      WORKFLOW_TARGET_CODE_KIND_RANK[a.kind] -
        WORKFLOW_TARGET_CODE_KIND_RANK[b.kind] ||
      (a.code < b.code ? -1 : a.code > b.code ? 1 : 0),
  )
}

/**
 * 対象日 (未選択時は today 1 件) を `CivilDate[]` へ正規化する。
 * 候補の有効性判定・失効警告の両方が同じ基準日集合を使う (issue #5219 Decisions #8)。
 */
function candidateDates(
  targetDates: readonly Date[],
  today: CivilDate,
): CivilDate[] {
  return targetDates.length > 0 ? targetDates.map(dateToCivilDate) : [today]
}

function isActiveOnAnyDate(
  candidate: Pick<WorkflowTargetCandidate, 'validFrom' | 'validTo'>,
  dates: readonly CivilDate[],
): boolean {
  const { validFrom, validTo } = candidate
  if (validFrom === undefined) return false
  return dates.some((d) => isActiveOn(validFrom, validTo, d))
}

/**
 * BG 必須モードの候補を絞り込む (issue #5219 Decisions #8, spec AC-WFW-003):
 * - エリア未選択時は絞り込まない。エリア選択時はそのエリアの候補のみに絞る
 * - いずれの場合も「選択済み対象日のいずれか 1 日で有効」 (`isActiveOn`) を
 *   満たす候補のみにする (対象日未選択時は today 基準)
 */
export function filterBgCandidates<
  T extends Pick<WorkflowTargetCandidate, 'areaCode' | 'validFrom' | 'validTo'>,
>(
  candidates: readonly T[],
  selectedAreaCodes: readonly AreaCode[],
  targetDates: readonly Date[],
  today: CivilDate = todayCivilDate(),
): T[] {
  const dates = candidateDates(targetDates, today)
  return candidates.filter((c) => {
    if (
      selectedAreaCodes.length > 0 &&
      !selectedAreaCodes.includes(c.areaCode)
    ) {
      return false
    }
    return isActiveOnAnyDate(c, dates)
  })
}

/**
 * 選択済みコードのうち、候補日 (選択済み対象日のいずれも無ければ today) の
 * **全て**で失効しているものが 1 つでもあるか判定する (issue #5219
 * Decisions #8: 対象日変更で自動除外はせず、warning のみ出す)。
 */
export function hasExpiredSelectedBg(
  selectedCodes: readonly string[],
  candidates: ReadonlyArray<
    Pick<WorkflowTargetCandidate, 'code' | 'validFrom' | 'validTo'>
  >,
  targetDates: readonly Date[],
  today: CivilDate = todayCivilDate(),
): boolean {
  const dates = candidateDates(targetDates, today)
  return selectedCodes.some((code) => {
    const candidate = candidates.find((item) => item.code === code)
    if (candidate === undefined) return false
    return !isActiveOnAnyDate(candidate, dates)
  })
}
